import { type ReactNode, useState } from 'react'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { Avatar as DsAvatar, BottomDrawer, Button } from '@systemeio/components'
import { ArrowLeftIcon, LogOut01Icon, Menu01Icon } from '@systemeio/icons'
import type { ParseKeys } from 'i18next'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { twMerge } from 'tailwind-merge'
import { MaintenanceBanner } from 'shared/components/maintenance-banner'
import NotificationMenu from 'shared/components/notification-menu/notification-menu'
import NotificationMenuMobile from 'shared/components/notification-menu/notification-menu-mobile'
import { featureFlagKeys } from 'shared/feature-flags/feature-keys'
import useUser, { isAuthorizedUser, isCustomer } from 'shared/hooks/use-user'
import { useGetLoginUrl } from 'shared/utils/url-utils'
import { useCommunityWithTopics } from 'modules/community/hooks/use-community-with-topics'
import { addPublicCustomClassNamePrefix } from 'modules/course/utils/add-class-prefix'
import ThemeEditorButton from 'modules/theme-editor/components/theme-editor-button'
import { useBrand } from 'modules/white-label/contexts/brand-context'
import Avatar from '../../shared/components/avatar'
import LanguageSwitcher, { MobileLanguageSwitcher } from './components/language-switcher'
import LogoLink from './components/logo-link'
import { Menu } from './menu'
import { SideMenuItem } from './menu/side-menu-item'
import { WorkspaceSelector } from './workspace-selector'

interface HeaderProps {
  logoSrc?: string
  className?: string
  desktopMenuItemClassName?: string
  desktopActiveMenuItemClassName?: string
  mobileMenuItemClassName?: string
  burgerClassName?: string
  menuIconClassName?: string
  activeMenuIconClassName?: string
  headerContainerClassName?: string
}

function Header({
  logoSrc,
  className,
  desktopMenuItemClassName,
  desktopActiveMenuItemClassName,
  mobileMenuItemClassName,
  burgerClassName,
  menuIconClassName,
  activeMenuIconClassName,
  headerContainerClassName,
}: HeaderProps): ReactNode {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const { user } = useUser()
  const { data: community } = useCommunityWithTopics()
  const { brand } = useBrand()
  const loginUrl = useGetLoginUrl()
  const isMaintenanceBannerEnabled = useFeatureIsOn(featureFlagKeys.enableMaintenanceBanner)
  const notificationIconClassName = menuIconClassName ?? 'text-text-primary hover:text-text-primary'
  const activeNotificationIconClassName = activeMenuIconClassName ?? 'text-text-primary'

  return (
    <nav
      className={twMerge(
        'sticky top-0 z-50 bg-bg-primary font-inter text-text-primary',
        className,
        addPublicCustomClassNamePrefix('header'),
      )}
    >
      <div
        className={twMerge(
          'z-10 mx-auto flex w-full items-center gap-3 px-4 py-2 sm:px-6 md:px-14',
          headerContainerClassName,
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          <LogoLink logoSrc={logoSrc || brand.logo?.path}>
            <img
              className={`max-h-8 ${addPublicCustomClassNamePrefix('header__logo')}`}
              src={logoSrc || brand.logo?.path}
              alt={brand.name}
            />
          </LogoLink>

          {community?.name && !(isAuthorizedUser(user) && user.workspaceMemberships.length > 0) ? (
            <span className="truncate font-bold">{community.name}</span>
          ) : null}
          <WorkspaceSelector
            className={addPublicCustomClassNamePrefix(`header__workspace-selector`)}
            position="top-menu"
          />
          <Menu
            className={`hidden min-w-0 shrink-0 items-center gap-1 lg:flex ${addPublicCustomClassNamePrefix('desktop-navbar')}`}
            menuItemClassName={desktopMenuItemClassName}
            menuItemActiveClassName={desktopActiveMenuItemClassName}
          />
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2 lg:gap-3">
          {isAuthorizedUser(user) ? (
            <div className="flex items-center gap-1 lg:hidden">
              <ThemeEditorButton className="p-0" />
              <NotificationMenuMobile
                isCustomer={isCustomer(user)}
                iconClassname={notificationIconClassName}
                activeIconClassname={activeNotificationIconClassName}
              />
              <button
                className={twMerge(
                  'flex size-10 items-center justify-center outline-none focus-visible:focus-ring-primary',
                  !user && 'animate-pulse',
                  addPublicCustomClassNamePrefix('header__burger-menu'),
                )}
                aria-label="Open Menu"
                onClick={() => setIsOpen(true)}
              >
                <Menu01Icon className={twMerge('size-6', burgerClassName)} />
              </button>
            </div>
          ) : (
            user && (
              <Button variant="primary" size="md" render={<a href={loginUrl} />}>
                {t('user.profile.menu.login')}
              </Button>
            )
          )}
          {isAuthorizedUser(user) && (
            <NotificationMenu
              className="hidden lg:flex"
              isCustomer={isCustomer(user)}
              iconClassname={notificationIconClassName}
              activeMenuIconClassname={activeNotificationIconClassName}
            />
          )}
          <div className="hidden h-full items-center justify-around gap-4 lg:flex">
            <Avatar />
            <LanguageSwitcher />
          </div>
        </div>
        {isAuthorizedUser(user) && (
          <BottomDrawer.Root open={isOpen} onOpenChange={setIsOpen}>
            <BottomDrawer.Portal>
              <BottomDrawer.Popup
                className={addPublicCustomClassNamePrefix('mobile-side-nav-menu__container')}
              >
                <BottomDrawer.Title className="sr-only">
                  {t('mobile_nav.back_to_page')}
                </BottomDrawer.Title>
                <BottomDrawer.Content className={twMerge('flex flex-col px-4 pb-4')}>
                  <BottomDrawer.Close
                    className={twMerge(
                      'flex w-fit cursor-pointer items-center gap-2 py-2',
                      mobileMenuItemClassName,
                      addPublicCustomClassNamePrefix('mobile-side-nav-menu__close-button'),
                    )}
                  >
                    <ArrowLeftIcon className="size-5" />
                    <span>{t('mobile_nav.back_to_page')}</span>
                  </BottomDrawer.Close>
                  <div
                    className={twMerge(
                      'flex items-center gap-3.5 py-4',
                      addPublicCustomClassNamePrefix('mobile-side-nave-menu__avatar'),
                    )}
                  >
                    <Link href="/profile/user-settings" className="flex items-center gap-3.5">
                      <DsAvatar
                        alt={`${user?.firstName} ${user?.lastName}`}
                        firstName={user?.firstName}
                        lastName={user?.lastName}
                        size="lg"
                        src={user?.avatarUrl || undefined}
                      />
                      <div className="flex flex-col">
                        <span className="text-base font-semibold">
                          {user?.firstName} {user?.lastName}
                        </span>
                        <span className="text-sm text-text-tertiary">
                          {t('mobile_nav.settings')}
                        </span>
                      </div>
                    </Link>
                  </div>

                  <Menu
                    isMobile
                    className={twMerge(
                      'flex flex-col gap-1 py-4',
                      addPublicCustomClassNamePrefix('mobile-navbar'),
                    )}
                    menuItemClassName={mobileMenuItemClassName}
                  />

                  <div className="mt-auto flex flex-col gap-3">
                    <WorkspaceSelector
                      className={addPublicCustomClassNamePrefix(
                        'mobile-side-nav-menu__workspace-selector',
                      )}
                      position="side-menu"
                    />
                    {user?.impersonator && (
                      <a href="/dashboard/back/entry?pretend_this_user=_exit">
                        <p className="cursor-pointer px-2 text-base">
                          {t('header.exit_impersonation')}
                        </p>
                      </a>
                    )}
                    <div className="flex items-center justify-between">
                      <SideMenuItem
                        className={twMerge(
                          'text-base',
                          mobileMenuItemClassName,
                          addPublicCustomClassNamePrefix('mobile-side-nav-menu__logout-button'),
                        )}
                        icon={<LogOut01Icon className="size-5" />}
                        name={'Logout' as ParseKeys}
                        href="/dashboard/logout"
                      />
                      <div
                        className={addPublicCustomClassNamePrefix(
                          'mobile-side-nav-menu__language-switcher',
                        )}
                      >
                        <MobileLanguageSwitcher />
                      </div>
                    </div>
                  </div>
                </BottomDrawer.Content>
              </BottomDrawer.Popup>
            </BottomDrawer.Portal>
          </BottomDrawer.Root>
        )}
      </div>

      {isMaintenanceBannerEnabled && <MaintenanceBanner data-from="header" />}
    </nav>
  )
}

export default Header
